import { createContext, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { CONTACT_INFO } from "@/data/anabiya";
import { useCurrency } from "@/context/CurrencyContext";

const CartContext = createContext(null);
const STORAGE_KEY = "anabiya_cart_v1";

const initial = { items: [] };

// item: { slug, title, price, image, size, colour, qty, variation, summary }
function reducer(state, action) {
  switch (action.type) {
    case "hydrate":
      return action.payload || state;
    case "add": {
      const p = action.payload;
      const key = (it) =>
        it.slug === p.slug && (it.summary || "") === (p.summary || "");
      const existing = state.items.find(key);
      const items = existing
        ? state.items.map((it) =>
            key(it) ? { ...it, qty: it.qty + p.qty } : it
          )
        : [...state.items, p];
      return { ...state, items };
    }
    case "remove":
      return {
        ...state,
        items: state.items.filter(
          (it) => !(it.slug === action.slug && (it.summary || "") === (action.summary || ""))
        ),
      };
    case "qty":
      return {
        ...state,
        items: state.items.map((it) =>
          it.slug === action.slug && (it.summary || "") === (action.summary || "")
            ? { ...it, qty: Math.max(1, action.qty) }
            : it
        ),
      };
    case "clear":
      return { ...state, items: [] };
    default:
      return state;
  }
}

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initial);
  const [open, setOpen] = useState(false);
  const { format, code } = useCurrency();

  const [hydrated, setHydrated] = useState(false);

  // hydrate — read once on mount, then mark ready
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) dispatch({ type: "hydrate", payload: JSON.parse(raw) });
    } catch {}
    setHydrated(true);
  }, []);
  // persist — but only AFTER hydration so we don't overwrite the saved cart
  // with the empty initial state on first render / browser reload.
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state, hydrated]);

  const totalItems = state.items.reduce((s, it) => s + it.qty, 0);
  const subtotal = state.items.reduce((s, it) => s + it.qty * it.price, 0);

  const addItem = (item) => {
    dispatch({ type: "add", payload: item });
    setOpen(true);
  };
  const removeItem = (slug, summary) =>
    dispatch({ type: "remove", slug, summary });
  const setQty = (slug, summary, qty) =>
    dispatch({ type: "qty", slug, summary, qty });
  const clear = () => dispatch({ type: "clear" });

  const checkoutViaWhatsApp = () => {
    if (!state.items.length) return;
    const lines = [
      "*Order — Anabiya Decor*",
      "",
      ...state.items.flatMap((it, i) => {
        const head = `${i + 1}. ${it.title} × ${it.qty} — ${format(it.price * it.qty)}`;
        return it.summary ? [head, `    ${it.summary}`] : [head];
      }),
      "",
      `Subtotal (${code}): ${format(subtotal)}`,
      "",
      "Please confirm availability, lead time and total (with taxes / shipping).",
    ];
    const url = `https://wa.me/${CONTACT_INFO.whatsapp}?text=${encodeURIComponent(
      lines.join("\n")
    )}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const value = useMemo(
    () => ({
      items: state.items,
      totalItems,
      subtotal,
      addItem,
      removeItem,
      setQty,
      clear,
      open,
      openCart: () => setOpen(true),
      closeCart: () => setOpen(false),
      checkoutViaWhatsApp,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, open, totalItems, subtotal]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};

export const formatINR = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
